// src/pages/Login.js
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import TwoFactorVerification from '../components/auth/TwoFactorVerification';
import MandatoryTwoFactorSetup from '../components/auth/MandatoryTwoFactorSetup';
import LoginCard from '../components/auth/LoginCard';
import LoginViewContent from '../components/auth/LoginViews';
import { Container, Box, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { supabase } from '../supabaseClient';
import { translateAuthError } from '../utils/authErrors';
import { useTheme } from '../ThemeContext';

// Doel na inloggen: volledige oorspronkelijke URL (incl. ?query en #hash)
function getRedirectTarget(from) {
  return from?.pathname && from.pathname !== '/login'
    ? `${from.pathname}${from.search || ''}${from.hash || ''}`
    : '/';
}

// Scherm voor een ingelogde gebruiker die (nog) niet volledig is aangemeld.
// Geeft null terug als het gewone inlogformulier getoond moet worden.
function renderAuthLevelScreen({ user, authLevel, onSuccess, onCancel, onRetry }) {
  // Als 2FA verificatie nodig is
  if (authLevel === 'needs_mfa') {
    return <TwoFactorVerification onSuccess={onSuccess} onBack={onCancel} />;
  }

  // Verplichte 2FA-setup (e-mail uit de sessie, niet uit het formulier: na een reload is dat leeg)
  if (authLevel === 'needs_setup') {
    return (
      <MandatoryTwoFactorSetup
        user={{ email: user.email }}
        onComplete={onSuccess}
        onCancel={onCancel}
      />
    );
  }

  if (authLevel === 'checking' || authLevel === 'full') {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (authLevel === 'error') {
    return (
      <Container maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Paper sx={{ p: 4, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Je aanmeldstatus kon niet worden gecontroleerd. Controleer je verbinding.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>Uitloggen</Button>
            <Button variant="contained" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return null;
}

export default function Login() {
  const [view, setView] = useState('login'); // 'login', 'forgotPassword', 'resetSent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, logout, user, authLevel, refreshAuthLevel } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(location.state?.message || null);

  const redirectTarget = getRedirectTarget(location.state?.from);

  // Check for password reset success message
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('passwordReset') === 'success') {
      setSuccess('Wachtwoord succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.');
      // Clean up URL
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate]);

  // De 2FA-status komt centraal uit AuthContext (authLevel). Login reageert er alleen op.
  useEffect(() => {
    if (!user) return;

    // Herstel-link uit e-mail: naar het reset-formulier
    if (window.location.hash.includes('type=recovery')) {
      navigate('/reset-password', { replace: true });
      return;
    }

    if (authLevel === 'full') {
      navigate(redirectTarget, { replace: true });
    }
  }, [user, authLevel, redirectTarget, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Attempt login
      const { error: loginError } = await login({ email, password });

      if (loginError) {
        setError(translateAuthError(loginError, 'Inloggen mislukt. Probeer het opnieuw.'));
        return;
      }

      // Login successful! The useEffect will handle the rest (MFA check, redirect, etc.)
      // No need to duplicate auth state logic here
    } catch (error) {
      console.error('Login error:', error);
      setError('Er is een fout opgetreden bij het inloggen');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    // "Onbekend account" tonen we als verstuurd, anders verraadt dit scherm welke accounts er zijn
    if (resetError && !/user not found/i.test(resetError.message || '')) {
      setError(translateAuthError(resetError));
    } else {
      setView('resetSent');
    }
  };

  // Na verificatie of setup: status opnieuw bepalen; het effect hierboven navigeert
  const handle2FASuccess = async () => {
    await refreshAuthLevel();
  };

  // "Terug"/"Ander account": echt uitloggen. Alleen de view wisselen hielp niet,
  // want met een aal1-sessie kwam het 2FA-scherm direct terug.
  const handleCancelMfa = async () => {
    await logout();
    setPassword('');
  };

  const authLevelScreen = user
    ? renderAuthLevelScreen({
        user,
        authLevel,
        onSuccess: handle2FASuccess,
        onCancel: handleCancelMfa,
        onRetry: () => refreshAuthLevel(),
      })
    : null;
  if (authLevelScreen) return authLevelScreen;

  return (
    <LoginCard
      theme={theme}
      view={view}
      success={success}
      setSuccess={setSuccess}
      error={error}
      setError={setError}
    >
      <LoginViewContent
        view={view}
        theme={theme}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loading={loading}
        setView={setView}
        onLogin={handleLogin}
        onForgotPassword={handleForgotPassword}
      />
    </LoginCard>
  );
}
